import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Button,
  Divider,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Restaurant as NutritionIcon,
  LocalPharmacy as SupplementIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  LocalDining as DiningIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import ShowChart from "@mui/icons-material/ShowChart";
import { styled } from "@mui/material/styles";
import SupplementPrescriptionDialog from "../../../../../components/organisms/SupplementPrescriptionDialog";
import useSupplements from "../../../../../hooks/useSupplements";
import useSupplementHistory from "../../../../../hooks/useSupplementHistory";
import NutritionCharts from "../NutritionCharts";
import NutritionRecentTable from "./NutritionRecentTable";
import NutritionTrackingSnapshotCard from "./NutritionTrackingSnapshotCard";
import MonthYearPicker from "../../../../../components/molecules/MonthYearPicker";
import useClientNutritionChartData from "../../../../../hooks/useClientNutritionChartData";
import {
  getMonthRange,
  getMonthRangeFromKey,
} from "../../../../../utils/monthDateRange";

const NUTRITION_SUB_OVERVIEW = "overview";
const NUTRITION_SUB_HISTORY = "history";
const TODAY_MONTH_KEY = getMonthRange(new Date()).key;
// Shared styled components (consistent with other tabs)
const StyledNutritionCard = styled(Card)(({ theme }) => ({
  height: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },
}));

const NutritionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const NutritionContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
}));

const MetricTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    border: "none",
    padding: theme.spacing(1),
  },
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 20,
  padding: "8px 20px",
  textTransform: "none",
  fontWeight: 600,
}));

// Enhanced Nutrition Tab Component
const EnhancedNutritionTab = ({
  client,
  getMealPlanById,
  nutritionChartData: overviewNutritionData,
  organizationId,
}) => {
  const [nutritionSubTab, setNutritionSubTab] = useState(NUTRITION_SUB_OVERVIEW);
  const [historyMonthKey, setHistoryMonthKey] = useState(TODAY_MONTH_KEY);
  // Add state for supplement dialog
  const [supplementDialogOpen, setSupplementDialogOpen] = useState(false);
  // Add state for edit supplement
  const [editingSupplement, setEditingSupplement] = useState(null);
  const { editClientSupplementData, getSupplementById } = useSupplements();
  const {
    loading: historyLoading,
    supplementCompliance,
    overallCompliance,
  } = useSupplementHistory({
    clientId: client?.uid,
    days: 30, // Look back 30 days for compliance data
  });

  const historyRange = useMemo(
    () => getMonthRangeFromKey(historyMonthKey),
    [historyMonthKey],
  );

  const { nutritionChartData: historyNutritionData } =
    useClientNutritionChartData({
      uid: client?.uid,
      organizationId,
      start: historyRange.start,
      end: historyRange.end,
      enabled: nutritionSubTab === NUTRITION_SUB_HISTORY,
    });

  const nutritionChartData =
    nutritionSubTab === NUTRITION_SUB_HISTORY
      ? historyNutritionData
      : overviewNutritionData;

  // State for meal plan
  const [clientMealPlan, setClientMealPlan] = useState(null);

  // Debug logging


  const handleOpenDialog = () => {

    setEditingSupplement(null);
    setSupplementDialogOpen(true);

  };

  const handleEditSupplement = (supplement) => {
    setEditingSupplement(supplement);
    setSupplementDialogOpen(true);
  };

  // Get supplementPrescriptions from client data or default to empty array
  const supplementPrescriptions = client?.supplementPrescriptions || [];
  const activeSupplements = supplementPrescriptions.filter(
    (s) => s.isActive === true,
  );

  // Use calculated compliance from the history data
  const calculatedOverallCompliance = historyLoading ? 0 : overallCompliance;

  // Enhance supplements with compliance data from history
  const enhancedSupplementPrescriptions = supplementPrescriptions.map(
    (supplement) => {
      const historyData = supplementCompliance[supplement.supplementId];
      return {
        ...supplement,
        compliance: historyData?.compliance || supplement.compliance || 0,
      };
    },
  );

  // Fetch meal plan data when client changes
  React.useEffect(() => {
    const fetchMealPlan = async () => {
      if (client?.mealPlanId) {
        try {
          const mealPlan = await getMealPlanById(client.mealPlanId);
          setClientMealPlan(mealPlan);
        } catch (error) {
          setClientMealPlan(null);
        }
      } else {
        setClientMealPlan(null);
      }
    };

    fetchMealPlan();
  }, [client, getMealPlanById]);



  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          mb: 0,
          mx: -3,
          mt: -3,
          px: 3,
          pt: 0,
        }}
      >
        <Tabs
          value={nutritionSubTab}
          onChange={(_, v) => setNutritionSubTab(v)}
          variant="fullWidth"
          aria-label="Nutrition sections"
          sx={{
            minHeight: 52,
            "& .MuiTab-root": {
              minHeight: 52,
              textTransform: "none",
              fontWeight: 600,
            },
          }}
        >
          <Tab
            value={NUTRITION_SUB_OVERVIEW}
            icon={<DashboardOutlined fontSize="small" />}
            iconPosition="start"
            label="Overview"
          />
          <Tab
            value={NUTRITION_SUB_HISTORY}
            icon={<ShowChart fontSize="small" />}
            iconPosition="start"
            label="History & charts"
          />
        </Tabs>
      </Box>

      {nutritionSubTab === NUTRITION_SUB_HISTORY && (
        <Box sx={{ p: 3, pt: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Nutrition history
            </Typography>
            <MonthYearPicker
              value={historyMonthKey}
              onChange={setHistoryMonthKey}
              showToday
            />
          </Stack>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <NutritionCharts
                client={client}
                useSharedNutritionSeries
                nutritionChartData={nutritionChartData}
              />
            </Grid>
            <Grid item xs={12} lg={5}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Days this month
              </Typography>
              <NutritionRecentTable
                nutritionChartData={nutritionChartData}
                maxRows={31}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {nutritionSubTab === NUTRITION_SUB_OVERVIEW && (
        <Box sx={{ p: 3, pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <NutritionTrackingSnapshotCard
                client={client}
                nutritionChartData={nutritionChartData}
              />
            </Grid>
        {/* Merged Supplement Tracker + Current Supplements */}
        <Grid item xs={12} lg={6}>
          <StyledNutritionCard>
            <NutritionHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <SupplementIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Supplement Tracker
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                >
                  Add Supplement
                </Button>
              </Stack>
            </NutritionHeader>
            <NutritionContent>
              <Stack spacing={3}>
                {/* Overview Stats */}
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Active Supplements
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {activeSupplements.length}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    of {supplementPrescriptions.length} total supplements
                  </Typography>
                </Box>

                {/* Compliance Rate */}
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Overall Compliance
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color={
                        supplementPrescriptions.length > 0
                          ? "success.main"
                          : "text.secondary"
                      }
                    >
                      {calculatedOverallCompliance}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={calculatedOverallCompliance}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "grey.200",
                    }}
                  />
                </Box>

                {supplementPrescriptions.length > 0 && (
                  <MetricTable size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Next Dose</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {activeSupplements.find(
                              (s) => s.nextDose && s.nextDose !== "Anytime",
                            )?.nextDose || "None"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Due Today</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="info.main"
                          >
                            {
                              activeSupplements.filter(
                                (s) => s.nextDose && s.nextDose !== "Anytime",
                              ).length
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </MetricTable>
                )}

                <Divider />

                {/* Supplement List (Merged from Current Supplements) */}
                <Typography variant="subtitle1" fontWeight="bold">
                  Current Supplements
                </Typography>

                {enhancedSupplementPrescriptions.length > 0 ? (
                  <List sx={{ width: "100%", p: 0 }}>
                    {enhancedSupplementPrescriptions.map((supplement) => {
                      const supplementInfo = getSupplementById(
                        supplement.supplementId,
                      );
                      return (
                        <ListItem
                          key={supplement.id}
                          sx={{
                            mb: 1,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            "&:hover": {
                              bgcolor: "grey.100",
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: supplement.isActive
                                  ? "success.main"
                                  : "warning.main",
                                width: 40,
                                height: 40,
                              }}
                            >
                              <SupplementIcon fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                  >
                                    {supplementInfo?.name ||
                                      "Unknown Supplement"}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {supplement.dosage}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {supplement.frequency}
                                    </Typography>
                                  </Stack>
                                </Box>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Chip
                                    label={
                                      supplement.isActive
                                        ? "active"
                                        : "inactive"
                                    }
                                    color={
                                      supplement.isActive
                                        ? "success"
                                        : "warning"
                                    }
                                    size="small"
                                  />
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      handleEditSupplement(supplement)
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Grid container spacing={2}>
                                  {supplement.instructions && (
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {supplement.instructions}
                                      </Typography>
                                    </Grid>
                                  )}
                                  <Grid item xs={12}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Updated:{" "}
                                        {supplement.updatedOn
                                          ? new Date(
                                              supplement.updatedOn.seconds *
                                                1000,
                                            ).toLocaleDateString()
                                          : "N/A"}
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                      >
                                        <Typography
                                          variant="body2"
                                          color={
                                            supplement.compliance >= 90
                                              ? "success.main"
                                              : supplement.compliance >= 70
                                                ? "warning.main"
                                                : "error.main"
                                          }
                                        >
                                          {supplement.compliance}% compliance
                                        </Typography>
                                        {supplement.compliance >= 90 ? (
                                          <CheckIcon
                                            color="success"
                                            fontSize="small"
                                          />
                                        ) : supplement.compliance >= 70 ? (
                                          <WarningIcon
                                            color="warning"
                                            fontSize="small"
                                          />
                                        ) : (
                                          <WarningIcon
                                            color="error"
                                            fontSize="small"
                                          />
                                        )}
                                      </Stack>
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "grey.50",
                      borderStyle: "dashed",
                      borderWidth: 2,
                      borderColor: "grey.300",
                    }}
                  >
                    <SupplementIcon
                      sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      No supplements assigned yet
                    </Typography>
                    <ActionButton
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleOpenDialog}
                    >
                      Add Your First Supplement
                    </ActionButton>
                  </Paper>
                )}
              </Stack>
            </NutritionContent>
          </StyledNutritionCard>
        </Grid>

        {/* Today's Nutrition - Updated to Meal Plan Display */}
        <Grid item xs={12} lg={6}>
          <StyledNutritionCard>
            <NutritionHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <NutritionIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Assigned Meal Plan
                </Typography>
              </Stack>
            </NutritionHeader>
            <NutritionContent>
              {clientMealPlan ? (
                <Stack spacing={3}>
                  {/* Meal Plan Overview */}
                  <Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {clientMealPlan.name}
                    </Typography>

                    {clientMealPlan.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {clientMealPlan.description}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Last updated:{" "}
                      {clientMealPlan.updatedOn
                        ? new Date(
                            clientMealPlan.updatedOn.seconds * 1000,
                          ).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Meal Plan Metrics */}
                  <MetricTable size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Daily Calories</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {clientMealPlan.totals?.totalkCals || 0} kcal
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Protein</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {clientMealPlan.totals?.totalProteins || 0} g
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Carbs</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {clientMealPlan.totals?.totalCarbs || 0} g
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Fats</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {clientMealPlan.totals?.totalFats || 0} g
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Fiber</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {clientMealPlan.totals?.totalFiber || 0} g
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Selected Recipes</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {clientMealPlan.selectedRecipes?.length || 0}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </MetricTable>

                  {clientMealPlan.selectedRecipes &&
                    clientMealPlan.selectedRecipes.length > 0 && (
                      <>
                        <Divider />

                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                          >
                            Selected Recipes
                          </Typography>
                          <List disablePadding>
                            {clientMealPlan.selectedRecipes.map((recipe) => (
                              <ListItem
                                key={recipe.id}
                                sx={{
                                  py: 1.5,
                                  px: 2,
                                  mb: 1,
                                  bgcolor: "grey.50",
                                  borderRadius: 1,
                                  "&:last-child": { mb: 0 },
                                }}
                              >
                                <ListItemText
                                  primary={recipe.name}
                                  secondary={
                                    <Stack direction="row" spacing={2} mt={0.5}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Days:{" "}
                                        {recipe.days
                                          .map((day) =>
                                            day.substring(0, 1).toUpperCase(),
                                          )
                                          .join(", ")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Servings: {recipe.servingsPerDay}
                                      </Typography>
                                    </Stack>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </>
                    )}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <DiningIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    No meal plan assigned to this client
                  </Typography>
                  <ActionButton variant="outlined" startIcon={<AddIcon />}>
                    Assign Meal Plan
                  </ActionButton>
                </Box>
              )}
            </NutritionContent>
          </StyledNutritionCard>
        </Grid>
          </Grid>
        </Box>
      )}

      <SupplementPrescriptionDialog
        client={client}
        open={supplementDialogOpen}
        supplementPrescription={editingSupplement}
        onDialogClosed={(newSupplement) => {
          setSupplementDialogOpen(false);

          if (newSupplement) {
            if (editingSupplement) {
              // Handle editing existing supplement
              editClientSupplementData(client, newSupplement);
            } else {
              editClientSupplementData(client, newSupplement);
            }
          }
          // Clear the editing state
          setEditingSupplement(null);
        }}
      />
    </Box>
  );
};

export default EnhancedNutritionTab;
